import { IconCheck } from "@tabler/icons-react";
import { cva } from "class-variance-authority";
import React from "react";
import { cn } from "@/lib/utils";

interface WizardProgressProps {
  currentStep: number;
  labels: readonly string[];
  descriptions?: readonly string[];
  variant?: "default" | "header" | "compact";
  className?: string;
  showLabels?: boolean;
}

const progressVariants = cva("flex items-center", {
  variants: {
    variant: {
      default:
        "shrink-0 h-[42px] gap-2.5 border-b border-line bg-table-footer px-[22px] text-[10px]",
      compact: "ml-auto shrink-0",
      header:
        "min-h-[37px] w-full min-w-0 max-w-full overflow-x-auto sm:flex-1 sm:basis-[660px] sm:max-w-[760px]",
    },
  },
  defaultVariants: { variant: "default" },
});

const indicatorVariants = cva("flex items-center justify-center rounded-full border", {
  variants: {
    variant: {
      default: "size-5 text-[11px] leading-[14px]",
      compact: "size-6 shrink-0 text-xs font-medium leading-4",
      header: "size-7 shrink-0 border-2 text-sm font-medium leading-5",
    },
    state: { complete: "", current: "", upcoming: "" },
  },
  compoundVariants: [
    {
      variant: "default",
      state: ["complete", "current"],
      className: "border-ink bg-ink text-white",
    },
    { variant: "default", state: "current", className: "font-semibold" },
    {
      variant: "default",
      state: "upcoming",
      className: "border-line bg-surface text-muted-foreground",
    },
    {
      variant: ["header", "compact"],
      state: "complete",
      className: "border-wizard-accent bg-wizard-accent text-canvas",
    },
    {
      variant: ["header", "compact"],
      state: "current",
      className: "border-wizard-accent bg-wizard-accent text-canvas",
    },
    {
      variant: ["header", "compact"],
      state: "upcoming",
      className: "border-subtle bg-surface text-muted-foreground",
    },
  ],
});

export function WizardProgress({
  currentStep,
  labels,
  descriptions,
  variant = "default",
  className,
  showLabels = false,
}: WizardProgressProps): React.ReactElement {
  return (
    <div
      data-slot="wizard-progress"
      role={variant !== "default" ? "list" : undefined}
      className={cn(progressVariants({ variant }), className)}
    >
      {labels.map((label, index) => (
        <React.Fragment key={label}>
          {index > 0 ? (
            <span
              aria-hidden="true"
              className={cn(
                "h-px bg-line",
                variant === "compact"
                  ? showLabels
                    ? "mx-3 w-5 shrink-0"
                    : "mx-2 w-4 shrink-0"
                  : variant === "header"
                    ? "mx-4 min-w-2 flex-1"
                    : "min-w-8 flex-1",
              )}
            />
          ) : null}
          <ProgressStep
            number={index + 1}
            label={label}
            description={descriptions?.[index]}
            active={currentStep}
            variant={variant}
            showLabels={showLabels}
          />
        </React.Fragment>
      ))}
    </div>
  );
}

function ProgressStep({
  number,
  label,
  description,
  active,
  variant,
  showLabels,
}: {
  number: number;
  label: string;
  description?: string;
  active: number;
  variant: "default" | "header" | "compact";
  showLabels: boolean;
}): React.ReactElement {
  const complete = number < active;
  const current = number === active;
  return (
    <div
      className={cn("flex shrink-0 items-center", variant === "header" ? "gap-3" : "gap-[7px]")}
      role={variant !== "default" ? "listitem" : undefined}
      aria-current={current ? "step" : undefined}
      title={variant !== "default" ? label : undefined}
    >
      <span
        aria-hidden={variant !== "default" ? true : undefined}
        className={cn(
          indicatorVariants({
            variant,
            state: complete ? "complete" : current ? "current" : "upcoming",
          }),
          showLabels && complete && "border-transparent bg-subtle text-ink",
        )}
      >
        {complete ? (
          <IconCheck className={variant === "header" ? "size-3.5" : "size-3"} aria-hidden />
        ) : (
          number
        )}
      </span>
      {variant !== "default" ? <span className="sr-only">{`${number}. `}</span> : null}
      <span
        className={cn(
          "flex flex-col",
          variant === "header" && "gap-px whitespace-nowrap",
          variant === "compact" && !showLabels && "sr-only",
        )}
      >
        <span
          className={cn(
            variant === "header"
              ? "text-sm font-medium leading-5 text-ink"
              : cn(
                  complete && (showLabels ? "text-muted-foreground" : "font-medium text-ink"),
                  showLabels && "text-xs leading-[18px]",
                  current && "font-semibold text-ink",
                  !complete && !current && "text-muted-foreground",
                ),
          )}
        >
          {label}
        </span>
        {description ? (
          <span
            className={cn(
              "font-normal text-muted-foreground",
              variant === "header" ? "text-xs leading-4" : "text-[10px] leading-[14px]",
            )}
          >
            {description}
          </span>
        ) : null}
      </span>
    </div>
  );
}
